import './lib/zod-config'
import { defineAbility } from '@casl/ability'
import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@fontsource/poppins/latin-400.css'
import '@fontsource/poppins/latin-400-italic.css'
import '@fontsource/poppins/latin-500.css'
import '@fontsource/poppins/latin-500-italic.css'
import '@fontsource/poppins/latin-600.css'
import '@fontsource/poppins/latin-600-italic.css'
import '@fontsource/poppins/latin-700.css'
import '@fontsource/poppins/latin-700-italic.css'
import 'photoswipe/style.css'
import 'photoswipe-dynamic-caption-plugin/photoswipe-dynamic-caption-plugin.css'
import './app.css'
import { OneStemTransitionAnnouncement } from './components/brand/OneStemTransitionAnnouncement'
import { ThemeProvider } from './hooks/useTheme'
import { normalizeDuplicatePathSlashes } from './lib/admin-url'
import { AbilityContext, abilityOptions, type AppAbility } from './lib/casl'
import { redirectLegacyAdminHost } from './lib/legacy-admin-host'
import { consumeOneStemTransitionMarker } from './lib/one-stem-transition'
import queryClient from './lib/queryClient'
import { router } from './router'

type AppProps = { showOneStemTransitionAnnouncement?: boolean }

export default function App({ showOneStemTransitionAnnouncement = false }: AppProps) {
  const ability = defineAbility<AppAbility>(() => {}, abilityOptions)
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="iris-ui-theme">
        <OneStemTransitionAnnouncement initiallyOpen={showOneStemTransitionAnnouncement} />
        <AbilityContext.Provider value={ability}>
          <RouterProvider router={router} context={{ queryClient, ability }} />
        </AbilityContext.Provider>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

const rootElement = document.getElementById('root')
const didNormalizeAdminPath = normalizeDuplicatePathSlashes()
const didRedirectLegacyAdminHost = !didNormalizeAdminPath && redirectLegacyAdminHost()
const showOneStemTransitionAnnouncement =
  !didNormalizeAdminPath && !didRedirectLegacyAdminHost && consumeOneStemTransitionMarker()

// @ts-expect-error index.html creates #root before importing this entry module; retain the existing bootstrap access.
if (!didNormalizeAdminPath && !didRedirectLegacyAdminHost && !rootElement.innerHTML) {
  // @ts-expect-error index.html creates #root before importing this entry module; retain the existing React mount.
  createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack)
    }),

    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Caught error', error, errorInfo.componentStack)
    }),

    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Recoverable error', error, errorInfo.componentStack)
    }),
  }).render(
    <StrictMode>
      <App showOneStemTransitionAnnouncement={showOneStemTransitionAnnouncement} />
    </StrictMode>
  )
}
